import { useEffect, useMemo } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import FormInputText from "../shared/FormInputText";
import FormSelect from "../shared/FormSelect";
import FormDate from "../shared/FormDate";
import {
    FormArrayEditor,
    ListSection,
    PARTIAL_INTERVAL_FIELD_CONFIG,
    PARTIAL_INTERVAL_FIELD_NAMES,
    PARTIAL_INTERVAL_OPTIONS,
    PARTIAL_INTERVAL_WASH,
    billingRateRowToCells,
    buildBillingRateRows,
    formatLockDate,
    getBillingRateHeaders,
    partialIntervalMethodValue,
} from "./BillingRateBuilderShared";
import { billingRateDefinitionSchema } from "./billingRateBuilderSchemas";

const getDefMetaFromForm = (def) => ({
    name: def?.componentReference?.component?.value ?? "",
    lockDate: formatLockDate(def?.componentReference?.lockDate),
});

const BillingRateDefinitionEditor = ({ prefix, componentNameOptions }) => {
    const { control } = useFormContext();
    const { errors } = useFormState({ control });
    return (
        <div className="billing-rate-builder__label-grid">
            <FormSelect
                name={`${prefix}.componentReference.component`}
                label="Component"
                control={control}
                options={componentNameOptions}
                errors={errors}
            />
            <FormDate
                name={`${prefix}.componentReference.lockDate`}
                label="Lock Date"
                control={control}
                errors={errors}
                showCurrentTZ={false}
            />
        </div>
    );
};

const CategoryRateEditBody = ({ index, componentsByName, billingCategoryRows, defaultLockDate, triggerValidationOnMount = false }) => {
    const { control, register, setValue, getValues, trigger } = useFormContext();
    const { errors } = useFormState({ control });

    // When editing an existing rate, surface validation errors immediately.
    useEffect(() => {
        if (triggerValidationOnMount) {
            trigger();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const category = useWatch({
        control,
        name: `billingCategoryRates.${index}`,
    }) ?? {};

    const componentNameOptions = useMemo(() => {
        const keys = componentsByName ? Array.from(componentsByName.keys()) : [];
        return keys.map((n) => ({ value: n, label: n }));
    }, [componentsByName]);

    const billingCategoryOptions = useMemo(
        () =>
            (billingCategoryRows ?? []).map((r) => ({
                value: r?.cdEntryTxt,
                label: `${r?.shortNm} (${r?.cdEntryTxt})`,
                additional: { shortName: r?.shortNm },
            })),
        [billingCategoryRows],
    );

    const { rowsByDef, anyTiered } = useMemo(
        () =>
            buildBillingRateRows(
                category?.billingRateDefinitions,
                componentsByName,
                getDefMetaFromForm,
            ),
        [category?.billingRateDefinitions, componentsByName],
    );
    const rateHeaders = getBillingRateHeaders(anyTiered);

    const prefix = `billingCategoryRates.${index}`;
    const method = partialIntervalMethodValue(category?.partialIntervalCalculationMethod);
    const activeFieldConfig = method ? PARTIAL_INTERVAL_FIELD_CONFIG[method] : null;

    const definitionsError =
        errors?.billingCategoryRates?.[index]?.billingRateDefinitions?.message ??
        errors?.billingCategoryRates?.[index]?.billingRateDefinitions?.root?.message;

    const handlePartialIntervalChange = (selected) => {
        const newMethod = partialIntervalMethodValue(selected);
        const newActiveName = newMethod ? PARTIAL_INTERVAL_FIELD_CONFIG[newMethod]?.name : null;
        PARTIAL_INTERVAL_FIELD_NAMES.forEach((fname) => {
            const fieldPath = `${prefix}.${fname}`;
            if (fname === newActiveName) {
                const current = getValues(fieldPath);
                if (current === "" || current == null) {
                    setValue(
                        fieldPath,
                        fname === PARTIAL_INTERVAL_FIELD_CONFIG[PARTIAL_INTERVAL_WASH]?.name ? 15 : 1,
                        { shouldDirty: true }
                    );
                }
            } else {
                setValue(fieldPath, "", { shouldDirty: true });
            }
        });
    };

    return (
        <div className="billing-rate-builder__modal-content-body">
            <div className="billing-rate-builder__label-grid">
                <FormSelect
                    name={`${prefix}.billingCategory`}
                    label="Billing Category"
                    control={control}
                    options={billingCategoryOptions}
                    errors={errors}
                />
                <FormSelect
                    name={`${prefix}.partialIntervalCalculationMethod`}
                    label="Partial Interval Calculation Method"
                    control={control}
                    options={PARTIAL_INTERVAL_OPTIONS}
                    errors={errors}
                    onSelectedHandler={handlePartialIntervalChange}
                />
                {activeFieldConfig && (
                    <FormInputText
                        key={activeFieldConfig.name}
                        name={`${prefix}.${activeFieldConfig.name}`}
                        label={activeFieldConfig.label}
                        register={register}
                        errors={errors}
                        disabled={true}
                    />
                )}
            </div>

            <ListSection
                label="Billing Party Percentages"
                headers={["Billing Party", "Bill Percentage"]}
                rows={(category?.billingPartyPercentages ?? []).map((bpp) => [
                    bpp?.billingParty,
                    bpp?.billPercentage,
                ])}
            />

            <FormArrayEditor
                name={`${prefix}.billingRateDefinitions`}
                label="Billing Rates"
                headers={rateHeaders}
                error={definitionsError}
                validateRow={(row) => billingRateDefinitionSchema.safeParse(row).success}
                getRows={(_def, i) =>
                    (rowsByDef[i] ?? []).map((r) => billingRateRowToCells(r, anyTiered))
                }
                makeNew={() => ({
                    componentReference: { component: null, lockDate: defaultLockDate ?? null },
                })}
                renderEditor={(rowPrefix) => (
                    <BillingRateDefinitionEditor
                        prefix={rowPrefix}
                        componentNameOptions={componentNameOptions}
                    />
                )}
            />
        </div>
    );
};

export default CategoryRateEditBody;
